/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { ExclamationCircleOutlined, TeamOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { t } from '@sportninja/common/i18n'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { getJerseyConflicts } from '@sportninja/common/features/game/attendance/utils/attendanceDerivations'
import React from 'react'
import { Link } from 'react-router-dom'
import EmptyStateBox from 'src/components/ts-components/EmptyStateBox'
import ScrollContainer from 'src/components/ts-components/ScrollContainer'
import AttendanceSection from './components/AttendanceSection'
import AttendanceSkeleton from './components/AttendanceSkeleton'
import AttendanceGameCard from './components/AttendanceGameCard'
import AttendanceStatusCard from './components/AttendanceStatusCard'
import { useAttendanceGameMeta } from './hooks/useAttendanceGameMeta'
import { useAttendanceJerseys } from './hooks/useAttendanceJerseys'
import { useAttendanceMutations } from './hooks/useAttendanceMutations'
import { useAttendanceSort } from './hooks/useAttendanceSort'
import { useGameAttendancePlayers } from './hooks/useGameAttendancePlayers'
import { useScoringContextSettings } from './hooks/useScoringContextSettings'
import {
  ELIGIBLE_GAME_SPARES_NOTE,
  ROSTER_SPARES_NOTE,
} from '@sportninja/common/features/game/attendance/constants'
import { isWaiverEnforced } from '@sportninja/common/features/game/attendance/utils/waiverLock'
import type { AttendancePlayer } from './types'
import {
  getEligibleGameSpares,
  getPlayersAndAffiliates,
  getPromotedCandidateRequestById,
} from './utils/sections'

export interface ManageTeamAttendanceModalContentProps {
  gameId: string
  rosterId: string
  // The game's show_waiver flag (from the game-list row).
  showWaiver: boolean
  // Viewer isn't a team official on this team: every control is display-only
  // and the Eligible Game Spares section is hidden…
  isReadOnly?: boolean
  // …except the availability icons of the viewer's own players.
  ownPlayerIds?: string[]
  soccerSportId?: number
}

// Fills the full-screen page under its header: the summary card stays put and
// the sections scroll. The page shell pads the sides on desktop; on narrow
// screens it drops its padding, so the body adds its own.
const containerStyle = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`

// ScrollContainer owns the overflow and draws the always-visible custom
// scrollbar; this lets it take the rest of the page height.
const scrollAreaStyle = css`
  flex: 1;
  min-height: 0;
  padding-bottom: 24px;
`

// The game card and the counts side by side; stacked once the page is too
// narrow for both to stay readable.
const summaryRowStyle = css`
  display: flex;
  gap: 16px;
  margin-bottom: 8px;

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`

// Same surface + frame as the attendance tables below.
const summaryPanelStyle = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1 1 0;
  min-width: 0;
  padding: 16px;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 8px;
  background-color: ${colors.SECONDARY_300};
`

// The game panel is a link to the game's page: no link colouring, and the
// row-hover surface so it reads as clickable.
const gamePanelLinkStyle = css`
  color: inherit;
  text-decoration: none;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover,
  &:focus-visible {
    color: inherit;
    background-color: ${colors.SECONDARY_200};
  }
`

// The panel already spaces the counts; drop the sidebar widget's top margin.
const countsStyle = css`
  margin-top: 0;
`

const sectionsStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const errorStyle = css`
  color: ${colors.ERROR_200};
  padding: 24px;
`

// Duplicate-jersey warning banner (dark modal). Mirrors the previous modal's
// alert styling.
const jerseyWarningStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;
  padding: 8px;
  border-radius: 8px;
  background-color: #360000;
  border: 1px solid ${colors.ERROR_200};
  color: ${colors.ERROR_100};
  font-size: 14px;
  line-height: 20px;
`

/**
 * Assembles the Manage Team Attendance modal body: summary card + the three
 * attendance sections (Roster, Roster Spares, Eligible Game Spares).
 */
const ManageTeamAttendanceModalContent: React.FC<
  ManageTeamAttendanceModalContentProps
> = ({
  gameId,
  rosterId,
  showWaiver,
  isReadOnly = false,
  ownPlayerIds = [],
  soccerSportId,
}) => {
  const { players, spareCandidates, loading, error } = useGameAttendancePlayers(
    { gameId, rosterId }
  )
  const { requireWaiverSignature, requireUniqueJerseyNumbers } =
    useScoringContextSettings(gameId)
  const waiverEnforced = isWaiverEnforced(showWaiver, requireWaiverSignature)
  const { isGenericSport, isSoccer, gameHeader, game } = useAttendanceGameMeta(
    gameId,
    soccerSportId
  )
  const jerseys = useAttendanceJerseys(gameId, game)
  const rosterSort = useAttendanceSort()
  const spareSort = useAttendanceSort()
  const candidateSort = useAttendanceSort()
  const {
    updateAttendanceStatus,
    promoteSpare,
    demoteSpare,
    confirmRosterSpare,
    withdrawSpare,
    updatePlayerNumber,
    updateStartingChange,
  } = useAttendanceMutations({ gameId, rosterId })

  const { skaters, affiliates } = getPlayersAndAffiliates(players)
  const eligibleSpares = getEligibleGameSpares(spareCandidates)
  // Spare candidates are an official's tool — a read-only viewer never sees them.
  const showEligibleSpares = !isReadOnly
  const promotedRequestById = getPromotedCandidateRequestById(spareCandidates)

  // getJerseyConflicts' local AttendancePlayer type models player_number as
  // `number | null`; this modal's AttendancePlayer allows `number | string`
  // (raw API shape). Structurally compatible at runtime — cast at the call site.
  const conflicts = getJerseyConflicts(
    players as unknown as Parameters<typeof getJerseyConflicts>[0]
  )
  // getJerseyConflicts returns occurrence-count-minus-one per number (0 = unique,
  // 1 = two players share it, …), matching the mobile consumer's `> 0` check.
  // So `> 0` flags any shared number, including the common two-player case.
  const duplicateJerseyNumbers = Object.keys(conflicts)
    .filter((key) => conflicts[key] > 0)
    .map((key) => Number(key))
  // Warn only when the game requires unique numbers (same gate as the per-row
  // red highlight), and only to someone who can fix it — not a read-only viewer.
  const showJerseyWarning =
    !isReadOnly &&
    requireUniqueJerseyNumbers &&
    duplicateJerseyNumbers.length > 0

  const onPressAttendanceStatus = (playerId: string, next: number | null) =>
    updateAttendanceStatus(playerId, next).catch(() => {})

  const onPressRow = (_player: AttendancePlayer) => {
    // No player-detail sheet on web yet.
  }

  const onStartingChange = (
    playerId: string,
    isStarting: boolean,
    otherGoalies: AttendancePlayer[]
  ) => updateStartingChange(playerId, isStarting, undefined, otherGoalies)

  if (loading) {
    return <AttendanceSkeleton />
  }

  if (error) {
    return <div css={errorStyle}>{error}</div>
  }

  return (
    <div css={containerStyle}>
      <div css={summaryRowStyle}>
        {gameHeader ? (
          // The game box opens that game's page.
          <Link
            to={`/game/${gameId}`}
            css={[summaryPanelStyle, gamePanelLinkStyle]}
            aria-label={capitalizeWords('Open game details')}
          >
            <AttendanceGameCard header={gameHeader} jerseys={jerseys} />
          </Link>
        ) : null}
        <div css={summaryPanelStyle}>
          <AttendanceStatusCard
            css={countsStyle}
            players={players}
            promotedRequestById={promotedRequestById}
          />
        </div>
      </div>
      {showJerseyWarning && (
        <div css={jerseyWarningStyle}>
          <ExclamationCircleOutlined />
          {`You Have ${duplicateJerseyNumbers.length} ${
            duplicateJerseyNumbers.length === 1 ? 'Player' : 'Players'
          } With The Same Jersey Number. Click A Number To Change It.`}
        </div>
      )}
      {/* Nothing in any section (no roster players, no eligible spares) →
          one boxed empty state instead of a blank body. Copy is shared with
          mobile's empty attendance screen. */}
      {players.length === 0 &&
      (!showEligibleSpares || eligibleSpares.length === 0) ? (
        <EmptyStateBox
          icon={<TeamOutlined />}
          title='No Players'
          subtitle={t('GameSetup:noPlayers', {})}
        />
      ) : (
        <ScrollContainer css={scrollAreaStyle}>
          <div css={sectionsStyle}>
            <AttendanceSection
              label='Roster'
              count={skaters.length}
              list={skaters}
              sort={rosterSort}
              waiverEnforced={waiverEnforced}
              requireUniqueJerseyNumbers={requireUniqueJerseyNumbers}
              duplicateJerseyNumbers={duplicateJerseyNumbers}
              isGenericSport={isGenericSport}
              isSoccer={isSoccer}
              allPlayers={players}
              isReadOnly={isReadOnly}
              ownPlayerIds={ownPlayerIds}
              hideConfirm
              reserveConfirmColumn={affiliates.length > 0}
              onPressAttendanceStatus={onPressAttendanceStatus}
              onPressRow={onPressRow}
              onUpdatePlayerNumber={updatePlayerNumber}
              onStartingChange={onStartingChange}
            />
            <AttendanceSection
              label='Roster Spares'
              note={ROSTER_SPARES_NOTE}
              count={affiliates.length}
              list={affiliates}
              sort={spareSort}
              waiverEnforced={waiverEnforced}
              requireUniqueJerseyNumbers={requireUniqueJerseyNumbers}
              duplicateJerseyNumbers={duplicateJerseyNumbers}
              isGenericSport={isGenericSport}
              isSoccer={isSoccer}
              allPlayers={players}
              isReadOnly={isReadOnly}
              ownPlayerIds={ownPlayerIds}
              availYesNoOnly
              isRosterSpares
              spareCandidatesLast
              promotedRequestById={promotedRequestById}
              onPressAttendanceStatus={onPressAttendanceStatus}
              onPressRow={onPressRow}
              onUpdatePlayerNumber={updatePlayerNumber}
              onStartingChange={onStartingChange}
              onDemoteSpare={demoteSpare}
              onConfirmRosterSpare={confirmRosterSpare}
            />
            {showEligibleSpares ? (
              <AttendanceSection
                label='Eligible Game Spares'
                note={ELIGIBLE_GAME_SPARES_NOTE}
                count={eligibleSpares.length}
                list={eligibleSpares}
                sort={candidateSort}
                waiverEnforced={waiverEnforced}
                requireUniqueJerseyNumbers={requireUniqueJerseyNumbers}
                duplicateJerseyNumbers={duplicateJerseyNumbers}
                isGenericSport={isGenericSport}
                isSoccer={isSoccer}
                allPlayers={players}
                isSpareCandidate
                onPressAttendanceStatus={onPressAttendanceStatus}
                onPressRow={onPressRow}
                onUpdatePlayerNumber={updatePlayerNumber}
                onStartingChange={onStartingChange}
                onPromoteSpare={promoteSpare}
                onDemoteSpare={demoteSpare}
                onWithdrawSpare={withdrawSpare}
              />
            ) : null}
          </div>
        </ScrollContainer>
      )}
    </div>
  )
}

export default ManageTeamAttendanceModalContent
